import { Component, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import {
  AiwynEditorConfig,
  AiwynEditorInitEvent,
  chipsToVariableTokens,
  createContentVariableElement,
  generateEditorConfig,
  repairClippedVariableTokens,
  variableTokensToChips,
} from '@aiwyn/foundations/components';
import { ConfigService } from '@anduin/config';
import { Store } from '@ngxs/store';
import { Editor } from '@tiptap/core';
import { map } from 'rxjs';
import { CommonCollectionsState } from '../../state';

@Component({
  selector: 'app-template-rich-text-editor',
  templateUrl: './template-rich-text-editor.component.html',
  styleUrl: './template-rich-text-editor.component.scss',
  standalone: false,
})
export class TemplateRichTextEditorComponent {
  private readonly store = inject(Store);
  private readonly configService = inject(ConfigService);

  public control = input.required<AbstractControl>();
  public placeholder = input<string>();

  protected readonly editorInstance = signal<Editor | null>(null);

  private readonly messageVariables$ = this.store.select(CommonCollectionsState.getMessageVariables);

  public init$ = this.messageVariables$.pipe(map((variables) => this.mapToEditorConfig(variables)));

  public variables$ = this.messageVariables$.pipe(map((vars) => [...vars.entries()]));

  private readonly displayNameByField = toSignal(
    this.messageVariables$.pipe(map((vars) => new Map([...vars.entries()].map(([displayName, field]) => [field, displayName])))),
    { initialValue: new Map<string, string>() },
  );

  /**
   * Inbound CVA transform: stored `${...}` tokens (the MVEL format) become atomic
   * chips so inline marks (bold/italic/...) can never split a token. Clipped
   * tokens from pre-chip content are repaired first so they chip correctly.
   */
  protected readonly parseValue = (value: string): string =>
    variableTokensToChips(repairClippedVariableTokens(value), this.displayNameByField());

  /** Outbound CVA transform: chips serialize back to literal `${...}` tokens. */
  protected readonly serializeValue = (html: string): string => chipsToVariableTokens(html);

  onEditorInit(event: AiwynEditorInitEvent): void {
    this.editorInstance.set(event.editor);
  }

  insertVariable(mappedField: string): void {
    const displayName = this.displayNameByField().get(mappedField) || mappedField;
    this.editorInstance()?.commands.insertContent(createContentVariableElement(mappedField, displayName).outerHTML);
  }

  private mapToEditorConfig(variables: Map<string, string>): AiwynEditorConfig {
    const standardConfigs: AiwynEditorConfig = generateEditorConfig(this.configService.invoiceStatementStyles);

    return {
      ...standardConfigs,
      height: 100,
      contentStyle: `
        /* editor-only style */
        body, p, .mce-content-body { color: #293c54; font-size: 0.875rem; }
      `,
      placeholder: this.removeHtmlTags(this.placeholder()),
      bubbleMenu: ['bold', 'italic', 'underline', 'strikethrough', 'separator', 'forecolor', 'separator', 'link'],
      setup: (editor: Editor) => {
        standardConfigs?.setup?.(editor);
      },
    };
  }

  private removeHtmlTags(htmlString: string): string {
    if (!htmlString) {
      return '';
    }
    return htmlString.replace(/(<([^>]+)>)/gi, '');
  }
}
